import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HexColorPicker } from "react-colorful";
import { programsApi } from "@/services/programsApi";
import { ProgramCard } from "@/pages/LedReminder/components/ProgramCard";
import { Program } from "@/pages/LedReminder/types.ts";

// Base form data function
const getBaseFormData = (): Program => {
  return {
    name: "",
    mode: "",
    active: true,
    color: "#00abff",
    startDay: "",
    stopDay: "",
    startTime: "",
    stopTime: "",
    type: "",
    _id: "",
  };
};

export const LedReminderPage = () => {
  const [formData, setFormData] = useState<Program>(getBaseFormData());
  const queryClient = useQueryClient();

  // Fetch programs with React Query
  const { data: programs = [] } = useQuery({
    queryKey: ["programs"],
    queryFn: programsApi.listPrograms,
  });

  // Mutations for adding, updating, and deleting programs
  const addProgramMutation = useMutation({
    mutationFn: programsApi.addNewProgram,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["programs"] });
      setFormData(getBaseFormData());
    },
  });

  const updateProgramMutation = useMutation({
    mutationFn: programsApi.updateProgram,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["programs"] });
      setFormData(getBaseFormData());
    },
  });

  const deleteProgramMutation = useMutation({
    mutationFn: programsApi.deleteProgram,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["programs"] });
      setFormData(getBaseFormData());
    },
  });

  const handleFormDataChange = (value: string | boolean, fieldName: string) => {
    setFormData((actualData) => ({ ...actualData, [fieldName]: value }));
  };

  const submitForm = (event: React.FormEvent) => {
    event.preventDefault();

    if (formData._id === "") {
      addProgramMutation.mutate(formData);
    } else {
      updateProgramMutation.mutate(formData);
    }
  };

  const deleteProgramInForm = (id: string) => {
    deleteProgramMutation.mutate(id);
  };

  const loadProgramInForm = (
    event: React.MouseEvent,
    programToLoad: Program
  ) => {
    event.stopPropagation();
    setFormData(programToLoad);
  };

  const getSelectForDay = (
    label: string,
    bindValue: "startDay" | "stopDay"
  ) => {
    return (
      <div className="form-control w-full max-w-xs">
        <label className="label">
          <span className="label-text">{label}</span>
        </label>
        <select
          className="select select-bordered"
          value={formData[bindValue] as string}
          onChange={(e) => handleFormDataChange(e.target.value, bindValue)}
        >
          <option value="" disabled>
            Sélectionner un jour
          </option>
          <option value="1">Lundi</option>
          <option value="2">Mardi</option>
          <option value="3">Mercredi</option>
          <option value="4">Jeudi</option>
          <option value="5">Vendredi</option>
          <option value="6">Samedi</option>
          <option value="0">Dimanche</option>
        </select>
      </div>
    );
  };

  const getNumericHourFromString = (timeInString: string) => {
    if (!timeInString) return new Date();

    const [hour, minute] = timeInString.split(":");
    return new Date().setHours(+hour, +minute);
  };

  const handleTimeChange = (time: string, field: "startTime" | "stopTime") => {
    handleFormDataChange(time, field);
  };

  return (
    <div className="flex flex-col lg:flex-row p-5 gap-5 justify-around">
      {/* Form Section */}
      <div className="card bg-base-100 shadow-xl w-full lg:w-1/2 mx-auto">
        <div className="card-body">
          <h2 className="card-title justify-center text-2xl mb-5">
            Gestion LED
          </h2>
          <form onSubmit={submitForm}>
            <div className="flex flex-col md:flex-row gap-6">
              {/* Left Side - Form Fields */}
              <div className="flex flex-col gap-4 flex-1">
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text">Nom :</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    value={formData.name}
                    onChange={(e) =>
                      handleFormDataChange(e.target.value, "name")
                    }
                  />
                </div>

                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text">Mode :</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    value={formData.mode}
                    onChange={(e) =>
                      handleFormDataChange(e.target.value, "mode")
                    }
                  />
                </div>

                {getSelectForDay("Jour start :", "startDay")}

                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text">Heure d'allumage :</span>
                  </label>
                  <input
                    type="time"
                    className="input input-bordered w-full"
                    value={formData.startTime}
                    onChange={(e) =>
                      handleTimeChange(e.target.value, "startTime")
                    }
                  />
                </div>

                {getSelectForDay("Jour stop :", "stopDay")}

                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text">Heure d'extinction :</span>
                  </label>
                  <input
                    type="time"
                    className="input input-bordered w-full"
                    value={formData.stopTime}
                    onChange={(e) =>
                      handleTimeChange(e.target.value, "stopTime")
                    }
                  />
                </div>
              </div>

              {/* Right Side - Active Checkbox and Color Picker */}
              <div className="flex flex-col gap-4 items-center flex-1">
                <div className="form-control">
                  <label className="label cursor-pointer">
                    <span className="label-text mr-4">Programme Actif :</span>
                    <input
                      type="checkbox"
                      className="toggle toggle-primary"
                      checked={formData.active}
                      onChange={(e) =>
                        handleFormDataChange(e.target.checked, "active")
                      }
                    />
                  </label>
                </div>

                <div className="flex flex-col items-center gap-2">
                  <span className="label-text">Couleur :</span>
                  <HexColorPicker
                    color={formData.color}
                    onChange={(hexColor) =>
                      handleFormDataChange(hexColor, "color")
                    }
                  />
                  <div
                    className="w-12 h-12 rounded-full mt-2 border border-gray-300"
                    style={{ backgroundColor: formData.color }}
                  />
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex justify-center gap-4 mt-6">
              <button
                type="submit"
                className="btn btn-success"
                disabled={
                  addProgramMutation.isPending ||
                  updateProgramMutation.isPending
                }
              >
                {formData._id !== "" ? "Modifier" : "Valider"}
              </button>

              <button
                type="button"
                className="btn btn-warning"
                onClick={() => setFormData(getBaseFormData())}
              >
                RaZ
              </button>

              {formData._id !== "" && (
                <button
                  type="button"
                  className="btn btn-error"
                  onClick={() => deleteProgramInForm(formData._id!)}
                  disabled={deleteProgramMutation.isPending}
                >
                  Supprimer
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Program List */}
      <div className="w-full lg:w-1/2 mt-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-4">
          {programs.map((program) => (
            <ProgramCard
              key={program._id}
              program={program}
              onClick={(e: React.MouseEvent) => loadProgramInForm(e, program)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
